import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { API, Empty } from "./shared";
import { Directory } from "./Directory";
import { SupplierOverview } from "./SupplierOverview";

const TABS = [
  ["all", "All suppliers", ["printer", "material_supplier", "finishing_partner", "agency"]],
  ["printer", "Printers / Converters", ["printer"]],
  ["material_supplier", "Material suppliers", ["material_supplier"]],
  ["finishing_partner", "Finishing partners", ["finishing_partner"]],
  ["agency", "Design & Creative agencies", ["agency"]],
];
const ROLE_LABEL = { printer: "Printer", material_supplier: "Material supplier", finishing_partner: "Finishing partner", agency: "Design agency" };
const th = { padding: "9px 12px", fontSize: 10.5, letterSpacing: ".06em", textTransform: "uppercase", color: "#64748b", fontWeight: 700, textAlign: "left", whiteSpace: "nowrap" };
const td = { padding: "10px 12px", fontSize: 12.5, verticalAlign: "top", borderTop: "1px solid #EEF2F7" };

function SupplierRow({ u, stages, showRole, onStage, onOpen }) {
  const loc = [u.location?.city, u.state || u.location?.state].filter(Boolean).join(", ");
  return (
    <tr data-testid={`supplier-row-${u.id}`} className="admin-design-row">
      <td style={td}>
        <button data-testid={`supplier-open-${u.id}`} className="supplier-open" onClick={() => onOpen(u.id)}>{u.company || u.name}</button>
        {u.is_test_account && <span style={{ fontSize: 9.5, fontWeight: 800, padding: "2px 6px", borderRadius: 4, background: "#FEF3C7", color: "#92400E", marginLeft: 6 }}>TEST</span>}
        <div style={{ fontSize: 11, color: "#64748b" }}>{u.email}</div>
      </td>
      {showRole && <td style={td}>{ROLE_LABEL[u.role]}</td>}
      <td style={td}>{u.phone || u.contact?.phone || "—"}{u.phone_verified && <span style={{ color: "#047857", marginLeft: 4 }}>✓</span>}</td>
      <td style={td}>{loc || "—"}</td>
      <td style={td}>{u.gstin || <span style={{ color: "#94a3b8" }}>—</span>}</td>
      <td style={td}>
        <select data-testid={`supplier-stage-${u.id}`} value={u.verification_stage || ""} onChange={e => onStage(u, e.target.value)}
          style={{ border: "1px solid #CBD5E1", borderRadius: 6, padding: "4px 6px", fontSize: 12, background: u.is_verified ? "#ECFDF5" : "#fff" }}>
          {!u.verification_stage && <option value="">—</option>}
          {stages.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </td>
      <td style={td}>{(u.subscription_tier || "—").replace(/_/g, " ")}</td>
      <td style={{ ...td, whiteSpace: "nowrap", color: "#475569" }}>{u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
    </tr>
  );
}

function SupplierList({ users, roles, stages, onStage, onOpen }) {
  const [q, setQ] = useState("");
  const s = q.trim().toLowerCase();
  const rows = users.filter(u => roles.includes(u.role) && (!s || [u.company, u.name, u.email, u.phone, u.location?.city, u.state].some(v => (v || "").toLowerCase().includes(s))));
  const showRole = roles.length > 1;
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "0 0 12px", gap: 10, flexWrap: "wrap" }}>
        <span data-testid="supplier-list-count" style={{ fontSize: 12.5, color: "#64748b" }}>{rows.length} supplier(s) · {rows.filter(u => u.is_verified).length} verified</span>
        <label style={{ display: "flex", alignItems: "center", gap: 6, border: "1px solid #CBD5E1", borderRadius: 8, padding: "5px 9px", background: "#fff" }}>
          <Search size={13} style={{ color: "#94a3b8" }}/>
          <input data-testid="supplier-list-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, email, phone, city" style={{ border: 0, outline: "none", fontSize: 12.5, width: 220, fontFamily: "inherit" }}/>
        </label>
      </div>
      {rows.length === 0 ? <Empty text="No suppliers here yet."/> : (
        <div data-testid="supplier-list" style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ background: "#F8FAFC" }}><tr>
              <th style={th}>Supplier</th>{showRole && <th style={th}>Type</th>}<th style={th}>Phone</th><th style={th}>Location</th><th style={th}>GSTIN</th><th style={th}>Verification</th><th style={th}>Plan</th><th style={th}>Joined</th>
            </tr></thead>
            <tbody>{rows.map(u => <SupplierRow key={u.id} u={u} stages={stages} showRole={showRole} onStage={onStage} onOpen={onOpen}/>)}</tbody>
          </table>
        </div>
      )}
    </>
  );
}

export function AdminSupplierNetwork({ role, onHireSuccess }) {
  const [tab, setTab] = useState("all");
  const [users, setUsers] = useState([]);
  const [stages, setStages] = useState([]);
  const [openId, setOpenId] = useState(null);
  const load = () => axios.get(`${API}/users`).then(r => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => {
    load();
    axios.get(`${API}/capabilities/reference`).then(r => setStages(r.data.verification_stages || [])).catch(() => {});
  }, []);
  const onStage = async (u, stage) => {
    try {
      await axios.patch(`${API}/users/${u.id}/verification`, { verification_stage: stage });
      toast.success(`${u.company || u.name}: ${stage}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update verification"); }
  };
  const count = (roles) => users.filter(u => roles.includes(u.role)).length;
  return (
    <>
      <div className="fin-tabs-wrap"><div className="fin-tabs" role="tablist" data-testid="supplier-network-tabs" style={{ flexWrap: "wrap" }}>
        {TABS.map(([id, label, roles]) => (
          <button key={id} role="tab" aria-selected={tab === id} data-testid={`supplier-tab-${id}`} className={tab === id ? "fin-tab active" : "fin-tab"} onClick={() => setTab(id)}>
            {label}<span data-testid={`supplier-tab-count-${id}`} style={{ fontSize: 10.5, fontWeight: 700, background: tab === id ? "#EAF1FF" : "#E2E8F0", color: tab === id ? "#1d4ed8" : "#475569", padding: "1px 7px", borderRadius: 999 }}>{count(roles)}</span>
          </button>
        ))}
      </div></div>
      {tab === "agency" ? <Directory role={role} onHireSuccess={onHireSuccess}/> : (
        <section className="content" data-testid={`supplier-panel-${tab}`}>
          <div className="section-heading"><div>
            <p className="eyebrow">Supplier network</p>
            <h2>{TABS.find(t => t[0] === tab)[1]}</h2>
          </div></div>
          <SupplierList key={tab} users={users} roles={TABS.find(t => t[0] === tab)[2]} stages={stages} onStage={onStage} onOpen={setOpenId}/>
        </section>
      )}
      <SupplierOverview uid={openId} onClose={() => setOpenId(null)} onSaved={load}/>
    </>
  );
}
